import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { clearCoursesCache } from '../courseApi';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  AdminStatusBadge,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminBreadcrumbs,
  AdminSectionHeading,
  AdminStatCard,
  ConfirmDialog,
  UnsavedChangesIndicator,
} from './components/AdminUiPrimitives';
import AdminAiAssistantModal from './components/AdminAiAssistantModal';
import {
  IconArchive,
  IconBook,
  IconCheckCircle,
  IconDelete,
  IconDescription,
  IconDraft,
  IconDuplicate,
  IconEdit,
  IconGeneral,
  IconMetadata,
  IconRelationships,
  IconSpark,
} from './components/AdminIcons';
import { useDirtyForm } from './components/useDirtyForm';
import { useAdminApi } from './hooks/useAdminApi';

interface Category {
  id: number;
  name: string;
  description?: string;
}

interface CategoryLookupOption {
  id: number;
  name: string;
  description?: string;
  parentCategoryId?: number | null;
  pathLabel: string;
}

interface Course {
  id: number;
  name: string;
  parentCourseId?: number | null;
  parentCourseName?: string | null;
  sortOrder: number;
  inforPagenav?: string | null;
  description: string;
  difficultyLevel: string;
  prerequisites: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  categories?: Category[];
}

type CourseStatus = 'Active' | 'Draft' | 'Archived';
type EditorMode = 'create' | 'edit';
type SortKey = 'name' | 'parent' | 'status' | 'updated';
type CoursesViewMode = 'grid' | 'hierarchy';

interface CourseFormData {
  name: string;
  inforPagenav: string;
  description: string;
  difficultyLevel: string;
  prerequisites: string;
  sortOrder: number;
  categoryIds: number[];
  status: CourseStatus;
}

interface CourseRow {
  id: number;
  name: string;
  parent: string;
  status: CourseStatus;
  updatedAt: string;
  updatedLabel: string;
  course: Course;
}

interface ConfirmDialogState {
  title: string;
  message: string;
  actions: Array<{
    label: string;
    variant?: 'default' | 'primary' | 'destructive';
    onClick: () => void;
    disabled?: boolean;
  }>;
}

const DEFAULT_PAGE_SIZE = 8;

const buildEmptyForm = (): CourseFormData => ({
  name: '',
  inforPagenav: '',
  description: '',
  difficultyLevel: 'Beginner',
  prerequisites: '',
  sortOrder: 1,
  categoryIds: [],
  status: 'Draft',
});

const normalizeFormForCompare = (data: CourseFormData) => JSON.stringify({
  ...data,
  name: data.name.trim(),
  inforPagenav: data.inforPagenav.trim(),
  description: data.description.trim(),
  prerequisites: data.prerequisites.trim(),
  sortOrder: data.sortOrder,
  categoryIds: [...data.categoryIds].sort((a, b) => a - b),
});

const statusFromCourse = (course: Course): CourseStatus => {
  if (course.isActive) {
    return 'Active';
  }

  return 'Archived';
};

const formatUpdated = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }

  return date.toLocaleString();
};

const formatDateShort = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const toCourseForm = (course: Course): CourseFormData => ({
  name: course.name,
  inforPagenav: course.inforPagenav ?? '',
  description: course.description,
  difficultyLevel: course.difficultyLevel,
  prerequisites: course.prerequisites ?? '',
  sortOrder: course.sortOrder > 0 ? course.sortOrder : 1,
  categoryIds: course.categories?.map((category) => category.id) || [],
  status: statusFromCourse(course),
});

const hasHtmlTags = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const ManageCourses: React.FC = () => {
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [courses, setCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<CategoryLookupOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncingExpanded, setSyncingExpanded] = useState(false);
  const [deletingCourseId, setDeletingCourseId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<CoursesViewMode>('hierarchy');
  const [expandedHierarchyCourseIds, setExpandedHierarchyCourseIds] = useState<Set<number>>(new Set());
  const [hierarchyRoots, setHierarchyRoots] = useState<Course[]>([]);
  const [hierarchyChildrenByParent, setHierarchyChildrenByParent] = useState<Record<number, Course[]>>({});
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [loadingHierarchyChildrenIds, setLoadingHierarchyChildrenIds] = useState<Set<number>>(new Set());
  const [hasLoadedFullCourses, setHasLoadedFullCourses] = useState(false);

  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);

  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [mobilePane, setMobilePane] = useState<'list' | 'details'>('list');
  const [mode, setMode] = useState<EditorMode>('edit');
  const [formData, setFormData] = useState<CourseFormData>(buildEmptyForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [descriptionPreviewExpanded, setDescriptionPreviewExpanded] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);

  const API_BASE_URL = `${config.apiBaseUrl}/api`;
  const isNarrowLayout = viewportWidth < 1140;
  const isSmallScreen = viewportWidth < 768;
  const { isDirty: isDirtyForm, markClean } = useDirtyForm(buildEmptyForm(), normalizeFormForCompare);
  const hasUnsavedChanges = isDirtyForm(formData);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const upsertCoursesCache = useCallback((incoming: Course[]) => {
    if (!incoming.length) {
      return;
    }

    setCourses((previous) => {
      const byId = new Map<number, Course>(previous.map((course) => [course.id, course]));
      incoming.forEach((course) => {
        const existing = byId.get(course.id);
        byId.set(course.id, existing ? { ...existing, ...course } : course);
      });
      return Array.from(byId.values());
    });
  }, []);

  const toCategoryPathOptions = useCallback((items: Array<{ id: number; name?: string; description?: string; parentCategoryId?: number | null }>): CategoryLookupOption[] => {
    if (!items.length) {
      return [];
    }

    const byId = new Map<number, { id: number; name?: string; description?: string; parentCategoryId?: number | null }>();
    items.forEach((item) => byId.set(item.id, item));

    const labelCache = new Map<number, string>();

    const buildPathLabel = (id: number): string => {
      const cached = labelCache.get(id);
      if (cached) {
        return cached;
      }

      const segments: string[] = [];
      const visited = new Set<number>();
      let current = byId.get(id);

      while (current && visited.add(current.id)) {
        const nodeName = (current.name || '').trim();
        const nodeDescription = (current.description || '').trim();
        if (nodeName) {
          segments.push(nodeDescription ? `${nodeName} - ${nodeDescription}` : nodeName);
        }

        if (!current.parentCategoryId) {
          break;
        }

        current = byId.get(current.parentCategoryId);
      }

      segments.reverse();
      const fallback = `Category ${id}`;
      const nextLabel = segments.length > 0 ? segments.join(' > ') : fallback;
      labelCache.set(id, nextLabel);
      return nextLabel;
    };

    return items
      .map((item) => {
        const name = (item.name || '').trim() || `Category ${item.id}`;
        return {
          id: item.id,
          name,
          description: item.description,
          parentCategoryId: item.parentCategoryId ?? null,
          pathLabel: buildPathLabel(item.id),
        };
      })
      .sort((first, second) => first.pathLabel.localeCompare(second.pathLabel));
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      if (!hasActiveOrganization) {
        setCategories([]);
        return;
      }

      const token = await getManagementToken();
      const response = await axios.get<Array<{ id: number; name?: string; description?: string; parentCategoryId?: number | null }>>(`${API_BASE_URL}/categories/lookup`, {
        headers: getOrganizationHeaders(token),
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      setCategories(toCategoryPathOptions(rows));
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    }
  }, [API_BASE_URL, getManagementToken, getOrganizationHeaders, hasActiveOrganization, toCategoryPathOptions]);

  const fetchCourses = useCallback(async (): Promise<Course[]> => {
    try {
      if (!hasActiveOrganization) {
        setCourses([]);
        setError('No organization selected. Please choose an organization first.');
        setLoading(false);
        return [];
      }

      setLoading(true);
      setError(null);
      const token = await getManagementToken();
      const response = await axios.get<Course[]>(`${API_BASE_URL}/courses`, {
        headers: getOrganizationHeaders(token),
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      setCourses(rows);
      setHasLoadedFullCourses(true);
      return rows;
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to load courses.');
      setCourses([]);
      setHasLoadedFullCourses(false);
      return [];
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const fetchHierarchyRoots = useCallback(async () => {
    try {
      if (!hasActiveOrganization) {
        setHierarchyRoots([]);
        setHierarchyChildrenByParent({});
        return;
      }

      setHierarchyLoading(true);
      const token = await getManagementToken();
      const response = await axios.get<Course[]>(`${API_BASE_URL}/courses`, {
        headers: getOrganizationHeaders(token),
        params: { rootOnly: true },
      });

      const rows = Array.isArray(response.data) ? response.data : [];
      setHierarchyRoots(rows);
      setHierarchyChildrenByParent({});
      setExpandedHierarchyCourseIds(new Set());
      upsertCoursesCache(rows);
    } catch (err) {
      console.error('Error fetching root courses:', err);
      setHierarchyRoots([]);
      setHierarchyChildrenByParent({});
    } finally {
      setHierarchyLoading(false);
    }
  }, [API_BASE_URL, getManagementToken, getOrganizationHeaders, hasActiveOrganization, upsertCoursesCache]);

  const fetchHierarchyChildren = useCallback(async (parentCourseId: number) => {
    if (hierarchyChildrenByParent[parentCourseId]) {
      return;
    }

    try {
      if (!hasActiveOrganization) {
        return;
      }

      setLoadingHierarchyChildrenIds((previous) => {
        const next = new Set(previous);
        next.add(parentCourseId);
        return next;
      });
      const token = await getManagementToken();
      const response = await axios.get<Course[]>(`${API_BASE_URL}/courses`, {
        headers: getOrganizationHeaders(token),
        params: { parentCourseId },
      });

      const rows = Array.isArray(response.data) ? response.data : [];
      setHierarchyChildrenByParent((previous) => ({ ...previous, [parentCourseId]: rows }));
      upsertCoursesCache(rows);
    } catch (err) {
      console.error('Error fetching child courses:', err);
      setHierarchyChildrenByParent((previous) => ({ ...previous, [parentCourseId]: [] }));
    } finally {
      setLoadingHierarchyChildrenIds((previous) => {
        const next = new Set(previous);
        next.delete(parentCourseId);
        return next;
      });
    }
  }, [API_BASE_URL, getManagementToken, getOrganizationHeaders, hasActiveOrganization, hierarchyChildrenByParent, upsertCoursesCache]);

  const fetchCourseDetails = useCallback(async (courseId: number): Promise<Course | null> => {
    try {
      if (!hasActiveOrganization) {
        return null;
      }

      const token = await getManagementToken();
      const response = await axios.get<Course>(`${API_BASE_URL}/courses/${courseId}`, {
        headers: getOrganizationHeaders(token),
      });

      const course = response.data;
      if (course) {
        upsertCoursesCache([course]);
        return course;
      }

      return null;
    } catch (err) {
      console.error('Error fetching course details:', err);
      return null;
    }
  }, [API_BASE_URL, getManagementToken, getOrganizationHeaders, hasActiveOrganization, upsertCoursesCache]);

  useEffect(() => {
    fetchCategories();
    void fetchHierarchyRoots();
  }, [fetchCategories, fetchHierarchyRoots]);

  useEffect(() => {
    if (viewMode !== 'grid' || hasLoadedFullCourses) {
      return;
    }

    void fetchCourses();
  }, [fetchCourses, hasLoadedFullCourses, viewMode]);

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === selectedCourseId) ?? null,
    [courses, selectedCourseId]
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const getParentLabel = useCallback((course: Course) => {
    if (course.parentCourseName) {
      return course.parentCourseName;
    }

    if (!course.categories || course.categories.length === 0) {
      return 'No parent';
    }

    return course.categories[0].name;
  }, []);

  const toCourseRow = useCallback((course: Course): CourseRow => ({
    id: course.id,
    name: course.name,
    parent: getParentLabel(course),
    status: statusFromCourse(course),
    updatedAt: course.updatedAt,
    updatedLabel: formatUpdated(course.updatedAt),
    course,
  }), [getParentLabel]);

  const rows = useMemo<CourseRow[]>(() => {
    return courses.map((course) => toCourseRow(course));
  }, [courses, toCourseRow]);

  const filteredRows = useMemo(() => {
    const search = searchValue.trim().toLowerCase();

    return rows.filter((row) => {
      if (search) {
        const haystack = [
          row.name,
          row.parent,
          row.status,
          row.course.difficultyLevel,
          row.course.inforPagenav || '',
        ]
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (statusFilter !== 'all' && row.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [rows, searchValue, statusFilter]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];

    sorted.sort((first, second) => {
      const direction = sortDirection === 'asc' ? 1 : -1;

      if (sortKey === 'updated') {
        const firstTime = new Date(first.updatedAt).getTime();
        const secondTime = new Date(second.updatedAt).getTime();
        return (firstTime - secondTime) * direction;
      }

      const leftValue = String(first[sortKey] || '').toLowerCase();
      const rightValue = String(second[sortKey] || '').toLowerCase();

      if (leftValue < rightValue) {
        return -1 * direction;
      }

      if (leftValue > rightValue) {
        return 1 * direction;
      }

      return 0;
    });

    return sorted;
  }, [filteredRows, sortDirection, sortKey]);

  const hierarchyRootRows = useMemo(() => {
    const search = searchValue.trim().toLowerCase();

    return hierarchyRoots
      .filter((course) => {
        if (search) {
          const haystack = [course.name, course.inforPagenav || '', course.difficultyLevel]
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(search)) {
            return false;
          }
        }

        if (statusFilter !== 'all' && statusFromCourse(course) !== statusFilter) {
          return false;
        }

        return true;
      })
      .sort((first, second) => first.name.localeCompare(second.name))
      .map((course) => toCourseRow(course));
  }, [hierarchyRoots, searchValue, statusFilter, toCourseRow]);

  const toggleHierarchyCourse = (courseId: number) => {
    setExpandedHierarchyCourseIds((previous) => {
      const next = new Set(previous);
      if (next.has(courseId)) {
        next.delete(courseId);
      } else {
        next.add(courseId);
      }
      return next;
    });
  };

  const renderHierarchyNode = (row: CourseRow, depth = 0): React.ReactNode => {
    const expanded = expandedHierarchyCourseIds.has(row.id);
    const isLoadingChildren = loadingHierarchyChildrenIds.has(row.id);
    const hasLoadedChildren = Object.prototype.hasOwnProperty.call(hierarchyChildrenByParent, row.id);
    const childRows = (hierarchyChildrenByParent[row.id] || []).map((child) => toCourseRow(child));

    return (
      <div key={`${depth}-${row.id}`} style={{ display: 'grid', gap: 4 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            border: selectedCourseId === row.id ? '1px solid #9fc0ff' : '1px solid #e6edf9',
            background: selectedCourseId === row.id ? '#edf4ff' : '#fff',
            borderRadius: 8,
            padding: '8px 10px',
            marginLeft: depth * 16,
          }}
        >
          <button
            type="button"
            onClick={() => {
              const opening = !expanded;
              toggleHierarchyCourse(row.id);
              if (opening && !hasLoadedChildren) {
                void fetchHierarchyChildren(row.id);
              }
            }}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#173159',
              fontWeight: 700,
              fontSize: '0.9rem',
              padding: 0,
              minWidth: 18,
            }}
            aria-label={expanded ? `Collapse ${row.name}` : `Expand ${row.name}`}
          >
            {expanded ? '▼' : '▶'}
          </button>

          <button
            type="button"
            onClick={() => handleSelectRow(row)}
            style={{
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'grid',
              gap: 2,
              width: '100%',
              padding: 0,
            }}
          >
            <span style={{ fontWeight: 700, color: '#142541' }}>{row.name}</span>
            <span style={{ fontSize: '0.78rem', color: '#607399' }}>
              {row.course.difficultyLevel} • {row.status}
            </span>
          </button>
        </div>

        {expanded && (
          <div style={{ display: 'grid', gap: 4 }}>
            {isLoadingChildren && (
              <div style={{ padding: '2px 8px', marginLeft: (depth + 1) * 16, color: '#607399', fontSize: '0.8rem' }}>
                Loading children...
              </div>
            )}

            {!isLoadingChildren && hasLoadedChildren && childRows.length === 0 && (
              <div style={{ padding: '2px 8px', marginLeft: (depth + 1) * 16, color: '#607399', fontSize: '0.8rem' }}>
                No direct children.
              </div>
            )}

            {!isLoadingChildren && childRows.map((childRow) => renderHierarchyNode(childRow, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const startIndex = (page - 1) * DEFAULT_PAGE_SIZE;
    return sortedRows.slice(startIndex, startIndex + DEFAULT_PAGE_SIZE);
  }, [page, sortedRows]);

  const clearSelectionPanel = useCallback(() => {
    const empty = buildEmptyForm();
    setMode('edit');
    setSelectedCourseId(null);
    setFormData(empty);
    markClean(empty);
    setFormErrors({});
  }, [markClean]);

  const syncFormForCourse = useCallback((course: Course | null, nextMode: EditorMode) => {
    if (!course) {
      const empty = buildEmptyForm();
      setMode('create');
      setSelectedCourseId(null);
      setFormData(empty);
      markClean(empty);
      setFormErrors({});
      return;
    }

    const draft = toCourseForm(course);
    setMode(nextMode);
    setSelectedCourseId(course.id);
    setFormData(draft);
    markClean(draft);
    setFormErrors({});
  }, [markClean]);

  const closeConfirmDialog = () => setConfirmDialog(null);

  const requestDiscardConfirmation = (
    title: string,
    messageText: string,
    onDiscard: () => void
  ) => {
    setConfirmDialog({
      title,
      message: messageText,
      actions: [
        {
          label: 'Keep Editing',
          variant: 'default',
          onClick: closeConfirmDialog,
        },
        {
          label: 'Discard Changes',
          variant: 'primary',
          onClick: () => {
            closeConfirmDialog();
            onDiscard();
          },
        },
      ],
    });
  };

  const withUnsavedGuard = (onProceed: () => void) => {
    if (!hasUnsavedChanges) {
      onProceed();
      return;
    }

    requestDiscardConfirmation(
      'Discard unsaved changes?',
      'You have unsaved changes to this course. If you continue, those changes will be lost.',
      onProceed
    );
  };

  const handleSelectRow = (row: CourseRow) => {
    withUnsavedGuard(() => {
      void (async () => {
        const detailedCourse = await fetchCourseDetails(row.id);
        syncFormForCourse(detailedCourse || row.course, 'edit');
        setMessage(null);
        if (isSmallScreen) {
          setMobilePane('details');
        }
      })();
    });
  };

  const handleCreateNew = () => {
    withUnsavedGuard(() => {
      syncFormForCourse(null, 'create');
      setMessage(null);
      setAiExpanded(false);
      if (isSmallScreen) {
        setMobilePane('details');
      }
    });
  };

  const handleRefresh = async () => {
    withUnsavedGuard(() => {
      void (async () => {
        if (viewMode === 'grid') {
          await fetchCourses();
        } else {
          await fetchHierarchyRoots();
        }
        await fetchCategories();
        setMessage('Latest course data loaded.');
      })();
    });
  };

  const handleSort = (columnKey: string) => {
    if (!['name', 'parent', 'status', 'updated'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as SortKey;
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection(nextKey === 'updated' ? 'desc' : 'asc');
  };

  const setField = <T extends keyof CourseFormData>(field: T, value: CourseFormData[T]) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFormErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }

      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      nextErrors.name = 'Name is required.';
    }

    if (!formData.description.trim()) {
      nextErrors.description = 'Description is required.';
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const upsertCourse = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = await getManagementToken();
      const payload = {
        name: formData.name.trim(),
        inforPagenav: formData.inforPagenav.trim() || null,
        description: formData.description,
        difficultyLevel: formData.difficultyLevel,
        prerequisites: formData.prerequisites.trim() || null,
        sortOrder: formData.sortOrder,
        categoryIds: formData.categoryIds,
        isActive: formData.status === 'Active',
      };

      if (mode === 'create' || !selectedCourseId) {
        const response = await axios.post<Course>(`${API_BASE_URL}/courses`, payload, {
          headers: getOrganizationHeaders(token),
        });
        setMessage('Course created successfully.');
        clearCoursesCache();
        const refreshed = await fetchCourses();

        const createdId = response.data?.id;
        if (createdId) {
          const createdCourse = refreshed.find((course) => course.id === createdId);
          if (createdCourse) {
            syncFormForCourse(createdCourse, 'edit');
          }
        }

        return;
      }

      await axios.put(`${API_BASE_URL}/courses/${selectedCourseId}`, payload, {
        headers: getOrganizationHeaders(token),
      });

      setMessage('Course updated successfully.');
      clearCoursesCache();
      const refreshed = await fetchCourses();
      const refreshedCourse = refreshed.find((course) => course.id === selectedCourseId);
      if (refreshedCourse) {
        syncFormForCourse(refreshedCourse, 'edit');
      }
    } catch (err: any) {
      console.error('Failed to save course', err);
      setError(err.response?.data?.message || err.message || 'Failed to save course.');
    } finally {
      setSaving(false);
    }
  };

  const syncExpandedCategories = async () => {
    if (!selectedCourse || mode !== 'edit') {
      return;
    }

    try {
      setSyncingExpanded(true);
      setError(null);
      setMessage(null);
      const token = await getManagementToken();
      const response = await axios.post(
        `${API_BASE_URL}/courses/${selectedCourse.id}/categories/expanded/rebuild`,
        {},
        { headers: getOrganizationHeaders(token) }
      );

      const rowCount = response.data?.rowCount;
      const suffix = typeof rowCount === 'number' ? ` (${rowCount} rows)` : '';
      setMessage(`Expanded category cache synced for ${selectedCourse.name}${suffix}.`);
    } catch (err: any) {
      console.error('Failed to sync expanded category cache', err);
      setError(err.response?.data?.message || err.message || 'Failed to sync expanded category cache.');
    } finally {
      setSyncingExpanded(false);
    }
  };

  const deleteCourseById = async (courseId: number) => {
    try {
      setDeletingCourseId(courseId);
      setError(null);
      const token = await getManagementToken();
      await axios.delete(`${API_BASE_URL}/courses/${courseId}`, {
        headers: getOrganizationHeaders(token),
      });

      clearCoursesCache();
      const refreshed = await fetchCourses();
      setSelectedIds((current) => current.filter((id) => id !== courseId));

      if (refreshed.length > 0) {
        syncFormForCourse(refreshed[0], 'edit');
      } else {
        clearSelectionPanel();
      }

      setMessage('Course deleted successfully.');
    } catch (err: any) {
      console.error('Failed to delete course', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete course.');
    } finally {
      setDeletingCourseId(null);
    }
  };

  const requestDeleteCourse = (row: CourseRow) => {
    setConfirmDialog({
      title: 'Delete course?',
      message: `Are you sure you want to delete "${row.name}"? This action may not be reversible.`,
      actions: [
        {
          label: 'Cancel',
          variant: 'default',
          onClick: closeConfirmDialog,
        },
        {
          label: deletingCourseId === row.id ? 'Deleting...' : 'Delete',
          variant: 'destructive',
          disabled: deletingCourseId !== null,
          onClick: () => {
            closeConfirmDialog();
            void deleteCourseById(row.id);
          },
        },
      ],
    });
  };

  const cancelChanges = () => {
    if (!hasUnsavedChanges) {
      if (mode === 'create') {
        clearSelectionPanel();
      } else if (selectedCourse) {
        syncFormForCourse(selectedCourse, 'edit');
      }
      return;
    }

    setConfirmDialog({
      title: 'Discard changes?',
      message: 'Your unsaved changes will be lost.',
      actions: [
        {
          label: 'Keep Editing',
          variant: 'default',
          onClick: closeConfirmDialog,
        },
        {
          label: 'Discard Changes',
          variant: 'primary',
          onClick: () => {
            closeConfirmDialog();
            setFormErrors({});
            if (mode === 'create') {
              clearSelectionPanel();
            } else if (selectedCourse) {
              syncFormForCourse(selectedCourse, 'edit');
            }
          },
        },
      ],
    });
  };

  const handleDuplicateCourse = (row: CourseRow) => {
    withUnsavedGuard(() => {
      const source = row.course;
      const duplicate = toCourseForm(source);
      duplicate.name = `${source.name} copy`;
      duplicate.inforPagenav = '';
      duplicate.status = 'Draft';
      setMode('create');
      setSelectedCourseId(null);
      setFormData(duplicate);
      markClean(buildEmptyForm());
      setFormErrors({});
      setMessage(`Prepared duplicate for ${source.name}. Save to create the copy.`);
      if (isSmallScreen) {
        setMobilePane('details');
      }
    });
  };

  const columns = useMemo<DataGridColumn<CourseRow>[]>(
    () => {
      const baseColumns: DataGridColumn<CourseRow>[] = [
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          width: isSmallScreen ? '65%' : '34%',
          render: (row) => (
            <div style={{ minWidth: isSmallScreen ? 0 : 180, width: '100%' }}>
              <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{row.name}</div>
              <div style={{ fontSize: '0.75rem', color: '#607399', marginTop: 2 }}>{row.course.difficultyLevel}</div>
            </div>
          ),
        },
      ];

      if (!isSmallScreen) {
        baseColumns.push({
          key: 'parent',
          label: 'Parent',
          sortable: true,
          width: '24%',
          render: (row) => row.parent,
        });
      }

      baseColumns.push({
        key: 'status',
        label: 'Status',
        sortable: true,
        width: isSmallScreen ? '20%' : '16%',
        render: (row) => <AdminStatusBadge status={row.status} />,
      });

      if (!isSmallScreen) {
        baseColumns.push({
          key: 'updated',
          label: 'Last Updated',
          sortable: true,
          width: '26%',
          render: (row) => row.updatedLabel,
        });
      }

      return baseColumns;
    },
    [isSmallScreen]
  );

  const filterConfigs = useMemo<FilterConfig[]>(() => {
    return [
      {
        key: 'status',
        label: 'Status',
        value: statusFilter,
        onChange: (value) => {
          setStatusFilter(value);
          setPage(1);
        },
        options: [
          { value: 'all', label: 'All Status' },
          { value: 'Active', label: 'Active' },
          { value: 'Draft', label: 'Draft' },
          { value: 'Archived', label: 'Archived' },
        ],
      },
    ];
  }, [statusFilter]);

  const actions: HeaderAction[] = [
    { key: 'create', label: 'Create New', variant: 'primary', onClick: handleCreateNew },
    { key: 'refresh', label: 'Refresh', variant: 'secondary', onClick: () => { void handleRefresh(); } },
    { key: 'import', label: 'Import', variant: 'ghost', onClick: () => setMessage('Import flow coming soon.'), disabled: true },
    { key: 'export', label: 'Export', variant: 'ghost', onClick: () => setMessage('Export flow coming soon.'), disabled: true },
    {
      key: 'ai',
      label: aiExpanded ? 'Hide AI Actions' : 'AI Actions',
      variant: 'ghost',
      onClick: () => setAiExpanded((current) => !current),
    },
  ];

  const detailsTitle = mode === 'create'
    ? 'Create Course'
    : selectedCourse
    ? `Edit: ${selectedCourse.name}`
    : 'Select a course';
  const hasEditableTarget = mode === 'create' || Boolean(selectedCourse);
  const showListPanel = !isSmallScreen || mobilePane === 'list';
  const showDetailsPanel = !isSmallScreen || mobilePane === 'details';
  const activeCount = rows.filter((row) => row.status === 'Active').length;
  const draftCount = rows.filter((row) => row.status === 'Draft').length;
  const archivedCount = rows.filter((row) => row.status === 'Archived').length;

  const rowActions: DataGridRowAction<CourseRow>[] = [
    {
      key: 'edit',
      label: 'Edit course',
      icon: <IconEdit size={14} />,
      onClick: (row) => handleSelectRow(row),
      isDisabled: () => saving || deletingCourseId !== null,
    },
    {
      key: 'duplicate',
      label: 'Duplicate course',
      icon: <IconDuplicate size={14} />,
      onClick: (row) => handleDuplicateCourse(row),
      isDisabled: () => saving || deletingCourseId !== null,
    },
    {
      key: 'delete',
      label: 'Delete course',
      icon: <IconDelete size={14} />,
      tone: 'destructive',
      onClick: (row) => requestDeleteCourse(row),
      isDisabled: () => saving || deletingCourseId !== null,
    },
  ];

  const handleMobilePaneSwitch = (nextPane: 'list' | 'details') => {
    if (nextPane === mobilePane) {
      return;
    }

    if (nextPane === 'list' && hasUnsavedChanges) {
      requestDiscardConfirmation(
        'Discard unsaved changes?',
        'You have unsaved changes to this course. If you continue, those changes will be lost.',
        () => {
          setMobilePane(nextPane);
        }
      );
      return;
    }

    setMobilePane(nextPane);
  };

  const breadcrumbCurrentLabel = mode === 'create'
    ? 'New Course'
    : selectedCourse?.name || 'Courses';

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          {
            label: 'Courses',
            onClick: () => {
              if (isSmallScreen) {
                setMobilePane('list');
              }
            },
          },
          {
            label: breadcrumbCurrentLabel,
            isCurrent: true,
            title: breadcrumbCurrentLabel,
          },
        ]}
      />

      <AdminPageHeader
        title="Courses"
        subtitle={`${viewMode === 'hierarchy' ? hierarchyRoots.length : courses.length} total • ${selectedIds.length} selected`}
        actions={actions}
      />

      {!hasActiveOrganization && (
        <div
          style={{
            borderRadius: 10,
            border: '1px solid #fcd34d',
            background: '#fffbeb',
            color: '#92400e',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          No organization selected. Select an organization to manage courses.
        </div>
      )}

      {(error || message) && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`,
            background: error ? '#fff1f2' : '#eff6ff',
            color: error ? '#b91c1c' : '#1d4ed8',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          {error || message}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        <AdminStatCard label="Total Courses" value={rows.length} icon={<IconBook size={15} />} active={statusFilter === 'all'} onClick={() => { setStatusFilter('all'); setPage(1); }} />
        <AdminStatCard label="Active" value={activeCount} icon={<IconCheckCircle size={15} />} accent="active" active={statusFilter === 'Active'} onClick={() => { setStatusFilter('Active'); setPage(1); }} />
        <AdminStatCard label="Draft" value={draftCount} icon={<IconDraft size={15} />} accent="draft" active={statusFilter === 'Draft'} onClick={() => { setStatusFilter('Draft'); setPage(1); }} />
        <AdminStatCard label="Archived" value={archivedCount} icon={<IconArchive size={15} />} accent="archived" active={statusFilter === 'Archived'} onClick={() => { setStatusFilter('Archived'); setPage(1); }} />
      </div>

      <AdminFilterBar
        searchValue={searchValue}
        onSearchChange={(value) => {
          setSearchValue(value);
          setPage(1);
        }}
        searchPlaceholder="Search courses by name, parent, level, slug..."
        filters={filterConfigs}
      />

      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: '1px solid #d8e3f6',
          borderRadius: 11,
          background: '#f6f9ff',
          padding: 4,
          width: 'fit-content',
        }}
      >
        <button
          type="button"
          className={`amd-btn ${viewMode === 'hierarchy' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
          onClick={() => setViewMode('hierarchy')}
        >
          Hierarchy View
        </button>
        <button
          type="button"
          className={`amd-btn ${viewMode === 'grid' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
          onClick={() => setViewMode('grid')}
        >
          Grid View
        </button>
      </div>

      {isSmallScreen && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid #d8e3f6',
            borderRadius: 11,
            background: '#f6f9ff',
            padding: 4,
            width: 'fit-content',
          }}
        >
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'list' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => handleMobilePaneSwitch('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'details' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => handleMobilePaneSwitch('details')}
          >
            Details
          </button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isNarrowLayout ? '1fr' : 'minmax(420px, 60%) minmax(320px, 40%)',
          gap: 12,
          minHeight: isNarrowLayout ? 'auto' : 560,
        }}
      >
        {showListPanel && <div style={{ minHeight: 0 }}>
          {(viewMode === 'grid' ? loading : hierarchyLoading) ? (
            <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff' }}>
              Loading courses...
            </div>
          ) : viewMode === 'grid' ? (
            <AdminDataGrid<CourseRow>
              rows={pagedRows}
              columns={columns}
              selectedRowId={selectedCourseId}
              onRowClick={handleSelectRow}
              selectedIds={selectedIds}
              onSelectedIdsChange={setSelectedIds}
              showSelection={!isSmallScreen}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              page={page}
              pageSize={DEFAULT_PAGE_SIZE}
              totalCount={sortedRows.length}
              onPageChange={setPage}
              emptyMessage="No records found."
              emptyActionLabel="Create Course"
              onEmptyAction={handleCreateNew}
              rowActions={rowActions}
            />
          ) : (
            <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
              {hierarchyRootRows.length === 0 ? (
                <div style={{ padding: 16, color: '#5f6f89', fontSize: '0.9rem' }}>No courses match the current filters.</div>
              ) : (
                <div style={{ display: 'grid', gap: 6, padding: 8 }}>
                  {hierarchyRootRows.map((rootRow) => renderHierarchyNode(rootRow, 0))}
                </div>
              )}
            </div>
          )}
        </div>}

        {showDetailsPanel && <div
          style={{
            border: '1px solid #dfe8f8',
            borderRadius: 12,
            background: '#fff',
            boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
            {isSmallScreen && (
              <button
                type="button"
                className="amd-btn amd-btn-ghost"
                style={{ marginBottom: 8 }}
                onClick={() => handleMobilePaneSwitch('list')}
              >
                Back to List
              </button>
            )}
            <h3
              style={{
                margin: 0,
                width: '100%',
                boxSizing: 'border-box',
                fontSize: '1.02rem',
                fontWeight: 800,
                color: '#173159',
                background: hasEditableTarget
                  ? 'linear-gradient(180deg, #f5f9ff 0%, #ebf3ff 100%)'
                  : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                border: hasEditableTarget ? '1px solid #cfe0fb' : '1px solid #d9e2ef',
                borderRadius: 10,
                padding: '9px 11px',
                letterSpacing: '0.01em',
                boxShadow: '0 2px 8px rgba(23, 49, 89, 0.08)',
              }}
            >
              {hasEditableTarget ? (mode === 'create' ? 'Create Course' : 'Editing Course') : detailsTitle}
            </h3>
            {hasEditableTarget ? (
              <div style={{ marginTop: 8, display: 'grid', gap: 3, fontSize: '0.8rem', color: '#5d739a' }}>
                <div style={{ color: '#1c2f54', fontWeight: 700 }}>{mode === 'create' ? formData.name || 'New Course' : selectedCourse?.name || '-'}</div>
                <div>Status: <span style={{ fontWeight: 700 }}>{mode === 'create' ? formData.status : statusFromCourse(selectedCourse as Course)}</span></div>
                <div>Last Updated: <span style={{ fontWeight: 700 }}>{formatDateShort(selectedCourse?.updatedAt || null)}</span></div>
                <UnsavedChangesIndicator dirty={hasUnsavedChanges} />
                {mode === 'edit' && selectedCourse && (
                  <div style={{ marginTop: 4 }}>
                    <button
                      type="button"
                      className="amd-btn amd-btn-secondary"
                      onClick={() => {
                        void syncExpandedCategories();
                      }}
                      disabled={syncingExpanded || saving || deletingCourseId !== null}
                    >
                      {syncingExpanded ? 'Syncing Expanded Categories...' : 'Sync Expanded Categories'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#61759a' }}>Select a course from the list or create a new one.</p>
            )}
          </div>

          <div style={{ padding: 14, overflowY: 'auto', minHeight: 0 }}>
            {!hasEditableTarget ? (
              <div
                style={{
                  minHeight: 260,
                  border: '1px dashed #c9d6ef',
                  borderRadius: 12,
                  background: '#f8fbff',
                  display: 'grid',
                  placeContent: 'center',
                  textAlign: 'center',
                  color: '#4f658f',
                  gap: 8,
                  padding: 16,
                }}
              >
                <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconBook size={26} /></div>
                <div style={{ fontWeight: 700 }}>Select a course from the list</div>
                <div style={{ fontSize: '0.82rem' }}>or</div>
                <button type="button" className="amd-btn amd-btn-primary" onClick={handleCreateNew}>Create a new course</button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 18 }}>
                <section style={{ display: 'grid', gap: 14, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconGeneral size={14} />} title="General" />
                  <label style={{ textAlign: 'left' }}>
                    <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Name *</span>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(event) => setField('name', event.target.value)}
                      placeholder="Course name"
                    />
                    {formErrors.name && <small style={{ color: '#b91c1c' }}>{formErrors.name}</small>}
                  </label>

                  <label style={{ textAlign: 'left' }}>
                    <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>URL Slug</span>
                    <input
                      type="text"
                      value={formData.inforPagenav}
                      onChange={(event) => setField('inforPagenav', event.target.value)}
                      placeholder="selective-school-preparation"
                    />
                  </label>

                  <div style={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr', gap: 10 }}>
                    <label style={{ textAlign: 'left' }}>
                      <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Difficulty</span>
                      <select
                        value={formData.difficultyLevel}
                        onChange={(event) => setField('difficultyLevel', event.target.value)}
                      >
                        <option value="Beginner">Beginner</option>
                        <option value="Intermediate">Intermediate</option>
                        <option value="Advanced">Advanced</option>
                      </select>
                    </label>

                    <label style={{ textAlign: 'left' }}>
                      <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Status</span>
                      <select
                        value={formData.status}
                        onChange={(event) => setField('status', event.target.value as CourseStatus)}
                      >
                        <option value="Active">Active</option>
                        <option value="Draft">Draft</option>
                        <option value="Archived">Archived</option>
                      </select>
                    </label>
                  </div>

                  <label style={{ textAlign: 'left' }}>
                    <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Sort Order</span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={formData.sortOrder}
                      onChange={(event) => setField('sortOrder', Number(event.target.value) || 1)}
                      placeholder="1"
                    />
                  </label>
                </section>

                <section style={{ display: 'grid', gap: 14, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconRelationships size={14} />} title="Relationships" />
                  <label style={{ textAlign: 'left' }}>
                    <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Parent Categories</span>
                    <div
                      style={{
                        border: '1px solid #c8d6ee',
                        borderRadius: 10,
                        maxHeight: 140,
                        overflowY: 'auto',
                        padding: 8,
                        background: '#fff',
                        display: 'grid',
                        gap: 6,
                      }}
                    >
                      {categories.length === 0 ? (
                        <div style={{ color: '#6b7280', fontSize: '0.82rem' }}>No categories available.</div>
                      ) : (
                        categories.map((category) => (
                          <label key={category.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
                            <input
                              type="checkbox"
                              checked={formData.categoryIds.includes(category.id)}
                              onChange={() => {
                                const exists = formData.categoryIds.includes(category.id);
                                setField(
                                  'categoryIds',
                                  exists
                                    ? formData.categoryIds.filter((id) => id !== category.id)
                                    : [...formData.categoryIds, category.id]
                                );
                              }}
                            />
                            {category.pathLabel}
                          </label>
                        ))
                      )}
                    </div>
                  </label>

                  <label style={{ textAlign: 'left' }}>
                    <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Prerequisites</span>
                    <input
                      type="text"
                      value={formData.prerequisites}
                      onChange={(event) => setField('prerequisites', event.target.value)}
                      placeholder="Optional prerequisites"
                    />
                  </label>
                </section>

                <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconDescription size={14} />} title="Description" />
                  <label style={{ textAlign: 'left' }}>
                    <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Description *</span>
                    <textarea
                      value={formData.description}
                      onChange={(event) => setField('description', event.target.value)}
                      rows={7}
                      placeholder="Course description (plain text or HTML)"
                      style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                    {formErrors.description && <small style={{ color: '#b91c1c' }}>{formErrors.description}</small>}
                  </label>

                  {hasHtmlTags(formData.description) && (
                    <div style={{ borderTop: '1px dashed #d6e0f2', paddingTop: 8 }}>
                      <button
                        type="button"
                        onClick={() => setDescriptionPreviewExpanded((current) => !current)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          fontSize: '0.76rem',
                          fontWeight: 700,
                          color: '#445b84',
                          cursor: 'pointer',
                        }}
                      >
                        Description Preview {descriptionPreviewExpanded ? '▲' : '▼'}
                      </button>
                      {descriptionPreviewExpanded && (
                        <div
                          className="html-content"
                          style={{ border: '1px solid #e2e8f5', borderRadius: 10, padding: 10, background: '#fcfdff', fontSize: '0.84rem', marginTop: 6 }}
                          dangerouslySetInnerHTML={{ __html: formData.description }}
                        />
                      )}
                    </div>
                  )}
                </section>

                <section style={{ display: 'grid', gap: 10, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconSpark size={14} />} title="AI Assistant" />
                  <div style={{ border: '1px solid #d9e2f2', borderRadius: 10, overflow: 'hidden' }}>
                    <button
                      type="button"
                      onClick={() => setAiExpanded((current) => !current)}
                      style={{
                        width: '100%',
                        border: 'none',
                        background: '#f6f9ff',
                        padding: '10px 11px',
                        textAlign: 'left',
                        fontWeight: 700,
                        color: '#233964',
                        cursor: 'pointer',
                      }}
                    >
                      AI Assistant {aiExpanded ? '▲' : '▼'}
                    </button>
                    {aiExpanded && (
                      <div style={{ padding: 10, background: '#fff', display: 'grid', gap: 7 }}>
                        <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setAiModalOpen(true)}>Generate Description</button>
                        <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setAiModalOpen(true)}>Improve Description</button>
                        <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setAiModalOpen(true)}>Generate Learning Outcomes</button>
                        <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setAiModalOpen(true)}>Suggest Categories</button>
                        <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setAiModalOpen(true)}>Generate Marketing Copy</button>
                      </div>
                    )}
                  </div>
                </section>

                <section style={{ display: 'grid', gap: 8, paddingBottom: 8 }}>
                  <AdminSectionHeading icon={<IconMetadata size={14} />} title="Metadata" />
                  <div style={{ display: 'grid', gap: 5, fontSize: '0.82rem', color: '#586f96' }}>
                    <div>Created: <strong>{formatDateShort(selectedCourse?.createdAt || null)}</strong></div>
                    <div>Last Updated: <strong>{formatDateShort(selectedCourse?.updatedAt || null)}</strong></div>
                    <div>Created By: <strong>System</strong></div>
                  </div>
                </section>
              </div>
            )}
          </div>

          {hasEditableTarget && (
            <div
              style={{
                borderTop: '1px solid #e4ecf9',
                background: 'rgba(255,255,255,0.98)',
                padding: '10px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                position: 'sticky',
                bottom: 0,
                zIndex: 4,
              }}
            >
              <button
                type="button"
                className="amd-btn amd-btn-ghost"
                onClick={() => selectedCourse && requestDeleteCourse({
                  id: selectedCourse.id,
                  name: selectedCourse.name,
                  parent: getParentLabel(selectedCourse),
                  status: statusFromCourse(selectedCourse),
                  updatedAt: selectedCourse.updatedAt,
                  updatedLabel: formatUpdated(selectedCourse.updatedAt),
                  course: selectedCourse,
                })}
                disabled={saving || deletingCourseId !== null || !selectedCourse}
              >
                {deletingCourseId !== null ? 'Deleting...' : 'Delete'}
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="amd-btn amd-btn-secondary"
                  onClick={cancelChanges}
                  disabled={saving || deletingCourseId !== null}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="amd-btn amd-btn-primary"
                  onClick={() => {
                    void upsertCourse();
                  }}
                  disabled={saving || deletingCourseId !== null}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>}
      </div>

      <AdminAiAssistantModal
        open={aiModalOpen}
        title="Course AI Assistant"
        promptLabel="What would you like the assistant to help with?"
        promptPlaceholder="Example: write a polished course description for an advanced selective school prep course."
        defaultPrompt="pls give the response as html. Give a detailed description for this course so that teachers, students, and parents can understand. Give the main categories for this as a list and give example questions for the categories. Also give some guidance on how to teach it, what steps are needed, and suggest ways to study this, including any resource suggestions. This is to show it in the course page."
        contextLabel="Course context"
        contextPlaceholder="Include the course name, level, category links, and any existing description or notes."
        initialContext={`Course name: ${formData.name || 'New course'}\nDifficulty: ${formData.difficultyLevel || 'Beginner'}\nStatus: ${formData.status}\nDescription: ${formData.description || ''}`}
        applyLabel="Apply to description"
        onClose={() => setAiModalOpen(false)}
        onApply={(generatedText) => {
          setField('description', generatedText);
          setMessage('AI-generated description applied.');
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        onClose={closeConfirmDialog}
        actions={confirmDialog?.actions || []}
      />
    </div>
  );
};

export default ManageCourses;
